<template>
    <div>
        <div class="grid grid-cols-12 gap-6">
            <div class="hidden md:col-span-1 md:block" />
            <div class="col-span-12 text-left md:col-span-10">
                <h2>Pravopis a jazyková správnost názvů</h2>
                <div class="mt-5 mb-5">
                    <img
                        src="../../public/img/articles/pravopis_a_jazykova_spravnost_nazvu.webp"
                        alt=""
                        class="mb-1 aspect-auto w-full object-cover"
                    />
                    <span class="source"
                        >Někde chceme být pravopisnější než pravopis sám;
                        správný tvar je Čertouská. / Zdroj: Marek Lašťovka</span
                    >
                </div>
                <p class="mb-5 font-bold">
                    Pojmenovávání veřejných prostranství není jen otázkou
                    kreativity, ale i jazykové správnosti. Zajímá vás, proč je
                    „Machnova“ špatně, nebo zda psát „U Komína“ či „U komína“?
                    Odpovědi najdete právě zde.
                </p>

                <p class="mb-5">
                    V pravidlech pro pojmenování veřejných prostranství byl vždy
                    kladen důraz na jazykovou a věcnou správnost a také na
                    srozumitelnost názvu. V těchto směrech se však problematické
                    názvy objevovaly jak dříve, tak i v nedávném období.
                </p>

                <p class="font-bold">„Růzveltova“</p>
                <p class="mb-5">
                    Co se týče srozumitelnosti či jednoduchosti, vyskytují se
                    problémy zejména u názvů podle jmen zahraničních osobností
                    (např. ulice

                    <span class="italic">
                        Rooseveltova, Pelléova, Wittgensteinova, Albrightové
                    </span>

                    aj.), ale problémy činí i osobnosti české s ne úplně
                    jednoznačně psaným příjmením. Např. jedna z ulic na Černém
                    Mostě se jmenuje
                    <span class="italic">Kuttelwascherova </span>
                    (čti Kutlvašrova) a je pojmenována po Karlu
                    Kuttelwascherovi.
                    <span class="italic">Náměstí Generála Kutlvašra</span>
                    je „také“ pojmenováno po Karlu Kutlvašrovi, ale jedná se o
                    zcela jinou osobu, s jiným pravopisem příjmení, avšak stejně
                    se vyslovujícím.
                </p>

                <p class="font-bold">Tvaroslovné a jazykové chyby</p>
                <p class="mb-5">
                    Do pražského názvosloví kromě nejasností či méně
                    srozumitelných jmen pronikly mnohdy i jazykové či
                    tvaroslovné chyby. Nejčastějšími takovými případy jsou názvy
                    komunikací podle obcí. Tak například ulice
                    <span class="italic">Bečvářská</span>
                    by se měla jmenovat
                    <span class="italic">Bečvárská</span> (obec Bečváry, nikoli
                    Bečváře),
                    <span class="italic">Citolibská</span>
                    by měla být s dlouhým í, tedy
                    <span class="italic">Cítolibská</span>
                    (obec Cítoliby),
                    <span class="italic">Hořínecká</span>
                    by měla být
                    <span class="italic">Hořínská</span>
                    (ves Hořín, nikoli Hořínec) atd. Omyly vznikly i při
                    pojmenovávání prostranství podle osob: ulice
                    <span class="italic">Fajmanové</span>
                    v Břevnově se má jmenovat
                    <span class="italic">Fajmonové</span>
                    (Josefa Fajmonová) či
                    <span class="italic">Destinové</span>
                    má být
                    <span class="italic">Destinnové.</span>
                    Ema Destinnová psala svůj pseudonym se dvěma n, avšak název
                    ulice byl úředně schválen s jedním n, a takto se tedy musí
                    užívat, nebude-li – opět úředně – opraven, jako se to stalo
                    s bratranci Veverkovými. Ulice
                    <span class="italic">Bratří Veverků</span>
                    byla roku 1999 oficiálně opravena, tedy přejmenována na
                    <span class="italic">Bratranců Veverkových.</span>
                    Zde se však dostáváme již k chybám nikoli jazykovým, ale
                    věcným. Název, který snoubí nepřesnost jazykovou i věcnou,
                    nese ulice
                    <span class="italic">Machnova.</span>
                    Machna z Miškovic, po níž se ulice nazývá, byla žena
                    (majitelka panství), takže by správný název měl znít
                    <span class="italic">Machnina.</span>
                    Tvaroslovně nepřesné jsou také názvy ulic podle planet v
                    tzv. Sluneční čtvrti. Tvary
                    <span class="italic">Saturnova</span>
                    či
                    <span class="italic">Merkurova</span>
                    by odpovídaly pojmenování podle antických bohů, kteří dali
                    planetám jména, avšak podle planet by názvy měly správně
                    znít
                    <span class="italic">Saturnská, Merkurská.</span>
                </p>

                <p class="font-bold">Názvy ulic s předložkou</p>
                <p class="mb-5">
                    Nejvíce diskutovanou otázkou ohledně pravopisu v názvech
                    ulic je bezpochyby psaní velkého/malého písmene po předložce
                    v předložkových názvech. Ústav pro jazyk český k psaní názvů
                    veřejných prostranství říká: Důvodem zavedení způsobu psaní
                    v Pravidlech českého pravopisu z roku 1993
                    <span class="italic"> K Rybníku, V Zálesí… </span>
                    (tedy první dvě písmena velká) bylo to, že ne vždy byl
                    pisatel schopen poznat, zda po předložce následuje původem
                    jméno obecné, nebo vlastní. Pravidla českého pravopisu však
                    také uvádí, že ve speciálních typech textů je možno
                    uplatňovat starou formu psaní. Tedy pokud jde o psaní
                    předložkových názvů ulic, původní způsob psaní se může
                    uplatňovat v textech odborných, tj. v těch, jež jsou
                    zaměřeny na původ místních a pomístních názvů, což je i
                    případ Pražského uličníku, a to i jeho digitální podoby.
                </p>

                <p class="font-bold">Jméno obecné nebo vlastní?</p>
                <p class="mb-5">
                    Problémem ovšem zůstává, že ani tvůrci této speciální
                    publikace nejsou podle dostupných pramenů schopni vždy
                    rozpoznat, zda se jedná o jméno vlastní, či obecné, a navíc
                    do věci vstupuje otázka, v jaké podobě byl název úředně
                    schválen. Příkladem může být název ulice
                    <span class="italic">U invalidovny</span>
                    – v poslední době se pojem Invalidovna, užívaný pro budovu
                    čp. 24 v Karlíně, stal jejím vlastním jménem (a bývá psán s
                    velkým písmenem), přestože původně, i v době schválení názvu
                    ulice (1947), šlo o obecné jméno vycházející z původního
                    účelu budovy. Úřední Registr územní identifikace, adres a
                    nemovitostí (RÚIAN) eviduje názvy ulic výhradně podle toho,
                    v jakém tvaru byly schváleny, bez ohledu na jazyková
                    pravidla. Rada hlavního města Prahy, až na některé méně
                    pochopitelné výjimky (<span class="italic"
                        >U Tabulky × K tabulce, Ke Statku</span
                    >), schvalovala názvy až do roku 2020 také podle původních
                    jazykových pravidel, která rozlišovala vlastní a obecná
                    jména v názvech. Roku 2020 pak Místopisná komise rady
                    hlavního města Prahy konstatovala, že v současné době při
                    navrhování předložkových názvů je druhé slovo psáno s velkým
                    počátečním písmenem, a to bez ohledu na to, jestli jde o
                    jméno obecné, nebo vlastní jméno, a proto jsou od té doby
                    schvalované názvy po předložce již s velkým písmenem.
                    Nicméně pro již pojmenovaná veřejná prostranství (včetně
                    prodlužovaných stávajících komunikací) stále platí, že jsou
                    ponechána beze změny, a to z mnoha praktických i
                    ekonomických důvodů. Pro pravopis názvů pražských ulic je
                    nyní závazná taková podoba, jakou uvádí RÚIAN.
                </p>

                <p class="font-bold">
                    Předložkové názvy vypovídají o původu názvu
                </p>

                <p class="mb-10 sm:mb-25">
                    Přestože tedy již od roku 2020 schvaluje rada hl. m. Prahy
                    názvy veřejných prostranství po předložkách s velkým
                    písmenem, najdete je v této aplikaci stále s takovou
                    velikostí písma, jak to odpovídá smyslu a původu názvu (tedy
                    nikoli
                    <span class="italic">V Křižovatce,</span>
                    ale
                    <span class="italic">V křižovatce,</span>
                    nikoli
                    <span class="italic">U Lihovaru,</span>
                    ale
                    <span class="italic">U lihovaru,</span>
                    nikoli
                    <span class="italic">U Komína,</span>
                    ale
                    <span class="italic">U komína</span>
                    aj.). Na tuto skutečnost, tedy že název konkrétní komunikace
                    byl již schválen s velkým písmenem po předložce, je vždy u
                    takového pojmenování zvlášť upozorněno (pokud je velké
                    písmeno proti věcnému smyslu – tedy nikoli např.
                    <span class="italic">
                        U Milíčova, Nad Šátečkem, K Hrnčířským loukám</span
                    >). Velikosti písmen nejsou podstatné při vyhledávání názvu
                    ulice, takže v tomto směru nijak nekomplikují funkci této
                    aplikace.
                </p>

                <!-- next chapters -->
                <div class="mb-10 flex justify-between border-t-1 pt-5">
                    <p class="mb-5 w-1/2 border-r-1 font-bold">
                        <nuxt-link
                            to="/clanky/vyvoj-oznaceni-ulic-v-terenu"
                            class="flex justify-start underline"
                        >
                            <img
                                src="../../public/img/left-arrow.png"
                                alt=""
                                class="order-1 mr-2 inline h-5 w-5"
                            />
                            <span class="order-2 mr-2"
                                >Vývoj označení ulic v terénu</span
                            >
                        </nuxt-link>
                    </p>
                    <p class="mb-5 w-1/2 text-right font-bold">
                        <nuxt-link
                            to="/clanky/prejmenovavani"
                            class="flex justify-end underline"
                        >
                            <span class="order-1 ml-2">Přejmenovávání</span>
                            <img
                                src="../../public/img/right-arrow.png"
                                alt=""
                                class="order-2 ml-2 inline h-5 w-5"
                            />
                        </nuxt-link>
                    </p>
                </div>
            </div>
            <div class="hidden md:col-span-1 md:block" />
        </div>

        <AppFooter />
    </div>
</template>

<script setup lang="ts"></script>

<style scoped></style>
